import { components } from "@appretio/api";
import { primitiveTokens, StatusChip } from "@appretio/ui-kit";
import type { ColDef } from "ag-grid-enterprise";

type AggLevelStatusOutput = components["schemas"]["AggLevelStatusOutput"];
type AggLevelStatus = AggLevelStatusOutput["agg_level_status"];

const STATUS_CONFIG: Record<
  AggLevelStatus,
  {
    label: string;
    variant: "success" | "warning" | "error";
    backgroundColor: string;
  }
> = {
  NEW: {
    label: "New level",
    variant: "success",
    backgroundColor: primitiveTokens.colors.green[100]
  },
  ABANDONED: {
    label: "Abandoned",
    variant: "error",
    backgroundColor: primitiveTokens.colors.red[100]
  },
  NEW_BUSINESS: {
    label: "New business",
    variant: "warning",
    backgroundColor: primitiveTokens.colors.yellow[100]
  }
};

function getCellStyleForStatus(data: AggLevelStatusOutput | undefined) {
  const backgroundColor = data?.agg_level_status
    ? STATUS_CONFIG[data.agg_level_status].backgroundColor
    : null;

  return {
    display: "flex",
    alignItems: "center",
    color: primitiveTokens.colors.purple[700],
    ...(backgroundColor ? { backgroundColor } : {})
  };
}

export const cubeOperationColumnDefs: ColDef<AggLevelStatusOutput>[] = [
  {
    field: "agg_level_status",
    headerName: "",
    width: 140,
    pinned: "left",
    cellStyle: ({ data }) => getCellStyleForStatus(data),
    cellRenderer: ({ value }: { value: AggLevelStatus }) => {
      const config = STATUS_CONFIG[value];
      if (!config) return null;

      return <StatusChip variant={config.variant} label={config.label} />;
    }
  },
  {
    field: "brand",
    headerName: "Brand",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "sub_brand",
    headerName: "Sub Brand",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "product_line",
    headerName: "Product Line",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "collection_type",
    headerName: "Collection Type",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "product_category",
    headerName: "Product Category",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "chain",
    headerName: "Chain",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "sales_location",
    headerName: "Sales Location",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "stock_location",
    headerName: "Stock Location",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "channel",
    headerName: "Channel",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "country",
    headerName: "Country",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  },
  {
    field: "collection_season_name",
    headerName: "Seasonality",
    flex: 1,
    cellStyle: ({ data }) => getCellStyleForStatus(data)
  }
];
